import { APP_FEATURE, useAppFeatures } from '@/lib/app-features';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { MailIcon, PlusIcon, XIcon } from 'lucide-react';
import {
  Avatar,
  Button,
  Field,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
  Tabs,
  TextInput,
  useDebouncedValue,
  EmptyState,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { readRedlineApprovers, updateRedlineApprovers, readNotificationEmails, saveNotificationEmails } from '@/lib/contracts';
import {
  avatarSrc,
  listUsers,
  lookupUsersByEmails,
  readSystemSettings,
  saveSystemSettings,
  type DirectoryPerson,
  CREDIT_LIST_PRICE_SETTING,
  DEFAULT_MINIMUM_MARGIN_PERCENT,
  MAX_MINIMUM_MARGIN_PERCENT,
  PRICING_MINIMUM_MARGIN_SETTING,
  type EmailListSetting,
  type ManagerSetting,
  type SystemSettingWrite,
  type SystemSettings,
} from '@/lib/events';
import { IntegrationsTab } from '@/pages/IntegrationsPage';
import { SuppliesTab } from '@/pages/settings-supplies';
import { ContactTypesTab } from '@/pages/settings-contact-types';
import { AssistantCategoriesTab } from '@/pages/settings-assistant-categories';
import { ClientPortalDefaultsTab } from '@/pages/settings-client-portal-tab';
import { EmailLogTab } from '@/pages/settings-email-log-tab';
import { FeaturesTab } from '@/pages/settings-features-tab';
import { WebsiteLeadsCard } from '@/pages/settings-website-leads-card';
import { FeedbackTab } from '@/pages/settings-feedback-tab';
import { ProgramManagerRulesTab } from '@/pages/settings-program-manager-rules-tab';
import { RolesTab } from '@/pages/settings-roles-tab';
import { canManageRoles, useMyIdentity } from '@/lib/roles';

const SETTINGS_TABS = ['general', 'program-manager-rules', 'notifications', 'teams', 'contact-types', 'assistant-categories', 'supplies', 'integrations', 'portal', 'features', 'roles', 'email-log', 'feedback'] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number];

/** Narrows a ?tab= value to a real tab; anything else lands on General. */
function asTab(value: string | null): SettingsTab {
  return (SETTINGS_TABS as readonly string[]).includes(value ?? '')
    ? (value as SettingsTab)
    : 'general';
}

/** Roles whose users the recipient lookup suggests. */
const INTERNAL_ROLES = ['superadmin', 'admin', 'internal'];

/** Loose email shape — enough to accept group inboxes like sales@. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The manager fields on the General tab, in display order. */
const MANAGER_FIELDS: readonly { key: ManagerSetting; label: string }[] = [
  { key: 'default-accounting-manager', label: 'Default accounting manager' },
  { key: 'default-contract-manager', label: 'Default contract manager' },
  { key: 'default-production-manager', label: 'Default production manager' },
  { key: 'default-program-manager', label: 'Default program manager' },
];

/** The email lists on the Notifications tab. */
const NOTIFICATION_LISTS: readonly { key: EmailListSetting; label: string; hint: string }[] = [
  {
    key: 'event-recap',
    label: 'Event recap notifications',
    hint: 'Emailed when a facilitator submits an event recap.',
  },
  {
    key: 'facilitator-assignment-notify',
    label: 'Facilitator assignment notifications',
    hint: 'Emailed when a facilitator is assigned to an event.',
  },
];

/** The email lists on the Teams tab. */
const TEAM_LISTS: readonly { key: EmailListSetting; label: string; hint: string }[] = [
  { key: 'charity-contacts', label: 'Charity contacts', hint: 'The charity coordination team.' },
  {
    key: 'production-contacts',
    label: 'Production contacts',
    hint: 'The production and logistics team.',
  },
  {
    key: 'smarthunts-contacts',
    label: 'SmartHunts contacts',
    hint: 'The SmartHunts program team.',
  },
];

/**
 * Platform settings, ported from the legacy Settings screen: default event
 * managers (General), notification recipients (Notifications), team contact
 * groups (Teams), and the Google/Salesforce connections (Integrations).
 * Admin-only — the API enforces it.
 *
 * Recipients are picked from the internal-user directory; a raw address is
 * accepted for group inboxes (sales@…) that have no user record. Stored
 * values stay plain emails — legacy consumers resolve them by email.
 *
 * @returns The settings page.
 */
export function SettingsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const isFeatureOn = useAppFeatures();
  const materialsOn = isFeatureOn(APP_FEATURE.materials);
  const categoriesOn = isFeatureOn(APP_FEATURE.assistantCategories);
  // Roles exist only while RBAC is switched on, for holders of manage_roles.
  const rolesOn = canManageRoles(useMyIdentity().data);
  // A tab whose feature is switched off is gone; a link to it lands on General.
  const requested = asTab(params.get('tab'));
  const tab =
    (requested === 'supplies' && !materialsOn) ||
    (requested === 'assistant-categories' && !categoriesOn) ||
    (requested === 'roles' && !rolesOn)
      ? 'general'
      : requested;

  const settings = useQuery({
    queryKey: ['system-settings'],
    queryFn: ({ signal }) => readSystemSettings(signal),
    enabled: tab !== 'integrations' && tab !== 'roles' && tab !== 'contact-types' && tab !== 'assistant-categories' && tab !== 'supplies' && tab !== 'portal' && tab !== 'features' && tab !== 'email-log' && tab !== 'feedback',
  });

  return (
    <section className="animate-portal-rise">
      <div data-guide="settings-heading"><PageHeading
        eyebrow="ADMINISTRATION"
        title="Settings"
        subtitle="Platform-wide defaults: who manages events, who gets notified, and what's connected."
      /></div>

      <div data-guide="settings-tabs"><Tabs<SettingsTab>
        label="Settings sections"
        items={[
          { key: 'general', label: 'General' },
          { key: 'program-manager-rules', label: 'Program manager rules' },
          { key: 'notifications', label: 'Notifications' },
          { key: 'teams', label: 'Teams' },
          { key: 'contact-types', label: 'Contact types' },
          ...(categoriesOn ? [{ key: 'assistant-categories' as const, label: 'Assistant categories' }] : []),
          ...(materialsOn ? [{ key: 'supplies' as const, label: 'Supplies' }] : []),
          { key: 'integrations', label: 'Integrations' },
          { key: 'portal', label: 'Client portal' },
          { key: 'features', label: 'Features' },
          ...(rolesOn ? [{ key: 'roles' as const, label: 'Roles' }] : []),
          { key: 'email-log', label: 'Email log' },
          { key: 'feedback', label: 'Feedback' },
        ]}
        active={tab}
        onChange={(next) =>
          setParams(
            () => {
              const nextParams = new URLSearchParams();
              nextParams.set('tab', next);
              return nextParams;
            },
            { replace: true },
          )
        }
      /></div>

      {tab === 'integrations' ? (
        <IntegrationsTab />
      ) : tab === 'contact-types' ? (
        <ContactTypesTab />
      ) : tab === 'assistant-categories' ? (
        <AssistantCategoriesTab />
      ) : tab === 'supplies' ? (
        <SuppliesTab />
      ) : tab === 'portal' ? (
        <ClientPortalDefaultsTab />
      ) : tab === 'features' ? (
        <FeaturesTab />
      ) : tab === 'roles' ? (
        <RolesTab />
      ) : tab === 'email-log' ? (
        <EmailLogTab />
      ) : tab === 'feedback' ? (
        <FeedbackTab />
      ) : settings.isPending ? (
        <LoadingState label="Loading settings…" />
      ) : settings.isError ? (
        <ErrorNotice title="Settings unavailable" message={errorMessage(settings.error)} />
      ) : tab === 'general' ? (
        <GeneralTab settings={settings.data} />
      ) : tab === 'program-manager-rules' ? (
        <ProgramManagerRulesTab settings={settings.data} />
      ) : (
        <EmailListsTab
          key={tab}
          settings={settings.data}
          lists={tab === 'notifications' ? NOTIFICATION_LISTS : TEAM_LISTS}
        />
      )}
    </section>
  );
}

/** Shared save mutation: writes settings and refreshes the query. */
function useSaveSettings(onSaved: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (writes: readonly SystemSettingWrite[]) => saveSystemSettings(writes),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      onSaved();
    },
  });
}

/** Internal-user suggestions for a lookup query. */
function useUserSuggestions(query: string): readonly DirectoryPerson[] {
  const debounced = useDebouncedValue(query);
  const trimmed = debounced.trim();
  const results = useQuery({
    queryKey: ['user-lookup', trimmed],
    queryFn: ({ signal }) => listUsers({ page: 1, search: trimmed }, signal),
    enabled: trimmed.length >= 2,
    staleTime: 60_000,
  });
  return (results.data?.people ?? []).filter(
    (person) => INTERNAL_ROLES.includes(person.role ?? '') && Boolean(person.email),
  );
}

/** A person's display name, falling back to their email. */
function personName(person: DirectoryPerson): string {
  return [person.firstName, person.lastName].filter(Boolean).join(' ') || person.email || '';
}

/** Resolves a set of emails to user cards, in one lookup. */
function useResolvedContacts(emails: readonly string[]): ReadonlyMap<string, DirectoryPerson> {
  const key = [...new Set(emails.map((email) => email.toLowerCase()))].sort();
  const lookup = useQuery({
    queryKey: ['user-emails', key],
    queryFn: ({ signal }) => lookupUsersByEmails(key, signal),
    enabled: key.length > 0,
    staleTime: 5 * 60_000,
  });
  return lookup.data ?? new Map();
}

/**
 * One recipient as a contact card: the resolved user's avatar and name, or —
 * for group inboxes with no user record — a mail glyph and the raw address.
 */
function ContactCard({
  email,
  person,
  onRemove,
}: {
  readonly email: string;
  readonly person: DirectoryPerson | undefined;
  readonly onRemove: () => void;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-2 rounded-portal-button border border-portal-line px-3 py-2">
      <span className="flex min-w-0 items-center gap-2.5">
        {person ? (
          <Avatar name={personName(person)} imageUrl={avatarSrc(person)} size={28} />
        ) : (
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-portal-bg text-portal-muted">
            <MailIcon aria-hidden className="size-3.5" />
          </span>
        )}
        <span className="min-w-0">
          <span className="block truncate text-portal-small font-bold text-portal-ink">
            {person ? personName(person) : email}
          </span>
          <span className="block truncate text-portal-micro text-portal-muted">
            {person ? email : 'Group inbox'}
          </span>
        </span>
      </span>
      <button
        type="button"
        aria-label={`Remove ${email}`}
        onClick={onRemove}
        className="shrink-0 rounded-portal-button p-1 text-portal-muted transition-colors hover:bg-portal-bg hover:text-rose-600"
      >
        <XIcon aria-hidden className="size-4" />
      </button>
    </div>
  );
}

/**
 * A lookup input over internal users. Typing searches the directory;
 * picking calls `onPick` with the user's email. A raw address that matches
 * no user can still be added — group inboxes have no user record.
 */
function RecipientLookup({
  onPick,
  exclude,
  placeholder,
  autoFocus,
}: {
  readonly onPick: (email: string) => void;
  readonly exclude: readonly string[];
  readonly placeholder?: string;
  readonly autoFocus?: boolean;
}): React.ReactElement {
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const suggestions = useUserSuggestions(query).filter(
    (person) => !exclude.includes(person.email ?? ''),
  );
  const trimmed = query.trim().toLowerCase();
  const isEmail = EMAIL_PATTERN.test(trimmed);
  const exactMatch = suggestions.some((person) => person.email?.toLowerCase() === trimmed);
  const open = trimmed.length >= 2;

  function pick(email: string): void {
    onPick(email);
    setQuery('');
  }

  return (
    <div ref={containerRef} className="relative">
      <TextInput
        value={query}
        placeholder={placeholder ?? 'Search people or enter an email…'}
        aria-label={placeholder ?? 'Search people or enter an email'}
        autoFocus={autoFocus}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            if (suggestions[0]?.email && !isEmail) pick(suggestions[0].email);
            else if (isEmail && !exclude.includes(trimmed)) pick(trimmed);
          }
        }}
      />
      {open ? (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-portal-button border border-portal-line bg-white shadow-portal-card">
          {suggestions.map((person) => (
            <button
              key={person._id}
              type="button"
              onClick={() => person.email && pick(person.email)}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-portal-bg"
            >
              <Avatar name={personName(person)} imageUrl={avatarSrc(person)} size={28} />
              <span className="min-w-0">
                <span className="block truncate text-portal-small font-bold text-portal-ink">
                  {personName(person)}
                </span>
                <span className="block truncate text-portal-micro text-portal-muted">
                  {person.email}
                </span>
              </span>
            </button>
          ))}
          {isEmail && !exactMatch && !exclude.includes(trimmed) ? (
            <button
              type="button"
              onClick={() => pick(trimmed)}
              className="flex w-full items-center gap-2 border-t border-portal-line px-3 py-2 text-left hover:bg-portal-bg"
            >
              <PlusIcon aria-hidden className="size-3.5 text-portal-muted" />
              <span className="text-portal-small">
                Use <span className="font-bold">{trimmed}</span>
              </span>
            </button>
          ) : null}
          {suggestions.length === 0 && !isEmail ? (
            <p className="px-3 py-2 text-portal-micro text-portal-muted">
              No matching internal users — enter a full email to add it directly.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** The General tab: default managers, picked from the directory. */
function GeneralTab({ settings }: { readonly settings: SystemSettings }): React.ReactElement {
  const initial = (): Record<ManagerSetting, string> =>
    Object.fromEntries(
      MANAGER_FIELDS.map((field) => [field.key, settings[field.key] ?? '']),
    ) as Record<ManagerSetting, string>;
  const [values, setValues] = useState(initial);
  const [saved, setSaved] = useState(false);
  const save = useSaveSettings(() => setSaved(true));

  const dirty = MANAGER_FIELDS.some((field) => values[field.key] !== (settings[field.key] ?? ''));
  const contacts = useResolvedContacts(Object.values(values).filter(Boolean));

  function set(key: ManagerSetting, value: string): void {
    setSaved(false);
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="mt-6">
      <div className="grid items-start gap-4 xl:grid-cols-2">
        {MANAGER_FIELDS.map((field) => (
          <Panel key={field.key} className="p-5">
            <h2 className="text-portal-card-title font-bold">{field.label}</h2>
            {values[field.key] ? (
              <div className="mt-3">
                <ContactCard
                  email={values[field.key]}
                  person={contacts.get(values[field.key].toLowerCase())}
                  onRemove={() => set(field.key, '')}
                />
              </div>
            ) : (
              <div className="mt-3">
                <RecipientLookup exclude={[]} onPick={(email) => set(field.key, email)} />
              </div>
            )}
          </Panel>
        ))}
        <CreditListPriceCard settings={settings} />
        <MinimumMarginCard settings={settings} />
        <RedlineApproversCard />
        <NotificationEmailsCard />
        <WebsiteLeadsCard settings={settings} />
      </div>

      {save.isError ? (
        <div className="mt-4">
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} />
        </div>
      ) : null}

      <div className="mt-4 flex items-center gap-3">
        <Button
          disabled={save.isPending || !dirty}
          onClick={() => {
            setSaved(false);
            save.mutate(
              MANAGER_FIELDS.map((field) => ({ name: field.key, value: values[field.key] })),
            );
          }}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          variant="secondary"
          disabled={save.isPending || !dirty}
          onClick={() => setValues(initial())}
        >
          Reset
        </Button>
        {saved && !dirty ? (
          <span className="text-portal-micro font-bold text-emerald-600">Saved</span>
        ) : null}
      </div>
    </div>
  );
}

/** A tab of email-list settings (Notifications and Teams share this). */
function EmailListsTab({
  settings,
  lists,
}: {
  readonly settings: SystemSettings;
  readonly lists: readonly { key: EmailListSetting; label: string; hint: string }[];
}): React.ReactElement {
  const initial = (): Record<string, readonly string[]> =>
    Object.fromEntries(lists.map((list) => [list.key, settings[list.key] ?? []]));
  const [values, setValues] = useState(initial);
  const [saved, setSaved] = useState(false);
  const save = useSaveSettings(() => setSaved(true));

  const dirty = lists.some(
    (list) => JSON.stringify(values[list.key]) !== JSON.stringify(settings[list.key] ?? []),
  );
  const contacts = useResolvedContacts(Object.values(values).flat());

  function update(key: EmailListSetting, emails: readonly string[]): void {
    setSaved(false);
    setValues((prev) => ({ ...prev, [key]: emails }));
  }

  return (
    <div className="mt-6">
      {/* CSS columns, not grid: cards pack under each other masonry-style,
          so a short card never leaves a hole beside a tall neighbour. */}
      <div className="gap-4 xl:columns-2">
        {lists.map((list) => (
          <div key={list.key} className="mb-4 break-inside-avoid">
            <RecipientList
              label={list.label}
              hint={list.hint}
              emails={values[list.key] ?? []}
              contacts={contacts}
              onChange={(emails) => update(list.key, emails)}
            />
          </div>
        ))}
      </div>

      {save.isError ? (
        <div className="mt-4">
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} />
        </div>
      ) : null}

      <div className="mt-4 flex items-center gap-3">
        <Button
          disabled={save.isPending || !dirty}
          onClick={() => {
            setSaved(false);
            save.mutate(lists.map((list) => ({ name: list.key, value: values[list.key] ?? [] })));
          }}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          variant="secondary"
          disabled={save.isPending || !dirty}
          onClick={() => setValues(initial())}
        >
          Reset
        </Button>
        {saved && !dirty ? (
          <span className="text-portal-micro font-bold text-emerald-600">Saved</span>
        ) : null}
      </div>
    </div>
  );
}

/** One recipient list: contact cards with remove, plus directory lookup. */
function RecipientList({
  label,
  hint,
  emails,
  contacts,
  onChange,
}: {
  readonly label: string;
  readonly hint: string;
  readonly emails: readonly string[];
  readonly contacts: ReadonlyMap<string, DirectoryPerson>;
  readonly onChange: (emails: readonly string[]) => void;
}): React.ReactElement {
  const [adding, setAdding] = useState(false);

  return (
    <Panel className="p-5">
      <h2 className="text-portal-card-title font-bold">{label}</h2>
      <p className="mt-0.5 text-portal-micro text-portal-muted">{hint}</p>

      <div className="mt-3 space-y-1.5">
        {emails.length === 0 ? (
          <EmptyState title="No recipients yet" message="Add who should hear about this; nobody is emailed until then." />
        ) : (
          emails.map((email) => (
            <ContactCard
              key={email}
              email={email}
              person={contacts.get(email.toLowerCase())}
              onRemove={() => onChange(emails.filter((value) => value !== email))}
            />
          ))
        )}
      </div>

      {adding ? (
        <div className="mt-3">
          <RecipientLookup
            autoFocus
            exclude={emails}
            onPick={(email) => {
              onChange([...emails, email]);
              setAdding(false);
            }}
          />
          <button
            type="button"
            onClick={() => setAdding(false)}
            className="mt-1.5 text-portal-micro font-bold text-portal-muted hover:text-portal-ink"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mt-3 flex items-center gap-1 text-portal-micro font-bold text-brand hover:underline"
        >
          <PlusIcon aria-hidden className="size-3.5" /> Add recipient
        </button>
      )}
    </Panel>
  );
}


/** The list price of one credit (§46): what a client pays before their preferred-pricing discount. */
function CreditListPriceCard({ settings }: { readonly settings: SystemSettings }): React.ReactElement {
  const stored = settings[CREDIT_LIST_PRICE_SETTING];
  const [value, setValue] = useState(stored ? String(stored) : '');
  const [saved, setSaved] = useState(false);
  const save = useSaveSettings(() => setSaved(true));
  const price = Number(value);
  const valid = value.trim() !== '' && Number.isFinite(price) && price > 0;
  const dirty = valid && price !== (stored ?? null);

  return (
    <Panel className="p-5">
      <h2 className="text-portal-card-title font-bold">List price per credit</h2>
      <p className="mt-1 text-portal-micro text-portal-muted">
        What a credit costs before a client&rsquo;s preferred-pricing discount. A client&rsquo;s agreement can carry its own price instead.
      </p>
      <form
        className="mt-3 flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (dirty) {
            setSaved(false);
            save.mutate([{ name: CREDIT_LIST_PRICE_SETTING, value: price }]);
          }
        }}
      >
        <Field label="Price (USD)" htmlFor="credit-list-price" className="w-40">
          <TextInput
            id="credit-list-price"
            type="number"
            min={0}
            step="0.01"
            value={value}
            onChange={(event) => {
              setSaved(false);
              setValue(event.target.value);
            }}
          />
        </Field>
        <Button type="submit" disabled={save.isPending || !dirty}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {saved ? <span className="text-portal-micro text-portal-muted">Saved.</span> : null}
      </form>
      {save.isError ? (
        <div className="mt-3">
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} />
        </div>
      ) : null}
    </Panel>
  );
}

/** The company minimum profit margin: the floor Sales → Pricing draws and checks discounts against. */
function MinimumMarginCard({ settings }: { readonly settings: SystemSettings }): React.ReactElement {
  const storedValue = settings[PRICING_MINIMUM_MARGIN_SETTING] ?? null;
  const stored = storedValue ?? DEFAULT_MINIMUM_MARGIN_PERCENT;
  const [value, setValue] = useState(String(stored));
  const [saved, setSaved] = useState(false);
  const save = useSaveSettings(() => setSaved(true));
  const percent = Number(value);
  const valid = value.trim() !== '' && Number.isFinite(percent) && percent >= 0 && percent <= MAX_MINIMUM_MARGIN_PERCENT;
  // Until an admin saves one, the default is shown and can be saved as is.
  const dirty = valid && (percent !== stored || storedValue === null);

  return (
    <Panel className="p-5">
      <h2 className="text-portal-card-title font-bold">Minimum profit margin</h2>
      <p className="mt-1 text-portal-micro text-portal-muted">
        The lowest margin a quote should keep. Pricing shows the lowest price and the largest discount that still clear it. Reps can try other margins there without changing this.
      </p>
      <form
        className="mt-3 flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (dirty) {
            setSaved(false);
            save.mutate([{ name: PRICING_MINIMUM_MARGIN_SETTING, value: percent }]);
          }
        }}
      >
        <Field label="Margin (%)" htmlFor="pricing-minimum-margin" className="w-40">
          <TextInput
            id="pricing-minimum-margin"
            type="number"
            min={0}
            max={MAX_MINIMUM_MARGIN_PERCENT}
            step="1"
            value={value}
            onChange={(event) => {
              setSaved(false);
              setValue(event.target.value);
            }}
          />
        </Field>
        <Button type="submit" disabled={save.isPending || !dirty}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {saved ? <span className="text-portal-micro text-portal-muted">Saved.</span> : null}
      </form>
      {save.isError ? (
        <div className="mt-3">
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} />
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * Who may approve contract redlines. The approve/decline emails go only to
 * these people, and once anyone is listed, applying redlines directly is
 * reserved for them. Saves immediately on add/remove.
 */
function RedlineApproversCard(): React.ReactElement {
  const queryClient = useQueryClient();
  const approvers = useQuery({
    queryKey: ['redline-approvers'],
    queryFn: ({ signal }) => readRedlineApprovers(signal),
  });
  const list = approvers.data ?? [];
  const contacts = useResolvedContacts(list);
  const save = useMutation({
    mutationFn: (next: string[]) => updateRedlineApprovers(next),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['redline-approvers'] }),
  });

  return (
    <Panel className="p-5">
      <h2 className="text-portal-card-title font-bold">Contract redline approvers</h2>
      <p className="mt-1 text-portal-micro text-portal-muted">
        Client redlines go to one of these people for approval before they can be applied.
        With no one listed, any internal user can apply redlines directly.
      </p>
      <div className="mt-3 space-y-2">
        {list.map((email) => (
          <ContactCard
            key={email}
            email={email}
            person={contacts.get(email.toLowerCase())}
            onRemove={() => save.mutate(list.filter((entry) => entry !== email))}
          />
        ))}
        <RecipientLookup exclude={list} onPick={(email) => save.mutate([...list, email])} />
      </div>
      {save.isError ? (
        <div className="mt-3">
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} />
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * Group inboxes notified on contract lifecycle moments: the contracts list
 * hears about sent + signed, the payments list about money received.
 * Empty list = that notification is off.
 */
function NotificationEmailsCard(): React.ReactElement {
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: ['notification-emails'],
    queryFn: ({ signal }) => readNotificationEmails(signal),
  });
  const [contractEmails, setContractEmails] = useState<string | null>(null);
  const [paymentEmails, setPaymentEmails] = useState<string | null>(null);
  const shownContracts = contractEmails ?? current.data?.contractEmails.join(', ') ?? '';
  const shownPayments = paymentEmails ?? current.data?.paymentEmails.join(', ') ?? '';
  const parse = (value: string): string[] =>
    value.split(',').map((entry) => entry.trim()).filter(Boolean);
  const save = useMutation({
    mutationFn: () =>
      saveNotificationEmails({
        contractEmails: parse(shownContracts),
        paymentEmails: parse(shownPayments),
      }),
    onSuccess: () => {
      setContractEmails(null);
      setPaymentEmails(null);
      void queryClient.invalidateQueries({ queryKey: ['notification-emails'] });
    },
  });

  return (
    <Panel className="p-5">
      <h2 className="text-portal-card-title font-bold">Contract &amp; payment notifications</h2>
      <p className="mt-1 text-portal-micro text-portal-muted">
        Internal group inboxes emailed when a contract is sent or signed, and when a
        payment is received. Comma-separate multiple addresses; leave blank to turn a
        notification off.
      </p>
      <div className="mt-3 space-y-3">
        <div>
          <label htmlFor="notify-contracts" className="mb-1 block text-portal-small text-portal-muted">
            Contracts (sent &amp; signed) — e.g. contracts@bestcorporateevents.com
          </label>
          <input
            id="notify-contracts"
            type="text"
            autoComplete="off"
            value={shownContracts}
            onChange={(event) => setContractEmails(event.target.value)}
            className="w-full rounded-portal-button border bg-white px-3 py-2 text-portal-small"
          />
        </div>
        <div>
          <label htmlFor="notify-payments" className="mb-1 block text-portal-small text-portal-muted">
            Payments — e.g. payments@bestcorporateevents.com
          </label>
          <input
            id="notify-payments"
            type="text"
            autoComplete="off"
            value={shownPayments}
            onChange={(event) => setPaymentEmails(event.target.value)}
            className="w-full rounded-portal-button border bg-white px-3 py-2 text-portal-small"
          />
        </div>
      </div>
      {save.isError ? (
        <div className="mt-2">
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} />
        </div>
      ) : null}
      <div className="mt-3">
        <Button
          size="compact"
          disabled={save.isPending || (contractEmails === null && paymentEmails === null)}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save recipients'}
        </Button>
      </div>
    </Panel>
  );
}
